import * as React from 'react';
import { action } from 'mobx';
import { observer } from 'mobx-react';
import {
    translate,
    ITranslationsCollection,
    addConfig,
    addContext,
    configConsumer
} from '@estee/elc-service';
import { VoiceSearch } from './VoiceSearch';
import { Spinner } from '@estee/elc-spinner';
import { SearchGnavInputFieldViewController } from '../../internal/controllers/SearchGnavInputFieldViewController';
import { diContainer, serviceNames } from '../../service-setup/diContainer';
import { classes } from '../../internal/utils/Classes';
import { Mobile, Desktop } from '../../internal/views/common/SearchMediaQueries';
import { SearchContentGrid } from '../../internal/views/content-search/SearchContentGrid';
import { SearchCloseActiveScreen } from '../../internal/views/SearchCloseActiveScreen';
import { SearchGrid } from '../../internal/views/SearchGrid';
import { SearchOverlayActiveScreen } from '../../internal/views/SearchOverlayActiveScreen';

import {
    SEARCH_PARAM,
    GNAV_SEARCH_TAG_BLOCKS,
    SEARCH_VIEW,
    TOGGLE_TYPE,
    PREDICTIVE_SEARCH_PARAM,
    SEARCH_REDIRECT_PARAM,
    TAB_TYPE,
    SEARCH_TYPE_PARAM,
    CONTENT_SEARCH_ITEMS_LIMIT,
    VIEW_TYPE
} from '../../internal/constants/Search';
import {
    IItem,
    IToutItem,
    IArticleObjItem,
    ITrendingTermItem,
    IPropsSearchConfig,
    IProductGridBreakpoints
} from '../../internal/interfaces/ISearchInterfaces';
import { mergeConfigs } from '../../internal/utils/ConfigUtils';
import {
    SearchTabs,
    SearchTab,
    SearchActiveScreenWrapper,
    SearchBoxWrapper,
    InputFieldSearch,
    ClearButton,
    GnavSearchButton,
    SearchResultsWrapper
} from '../../internal/views/styled-components/SearchGnavStyledComponents';
import { formatCount } from '../../internal/utils/FormatCount';
import { SearchResultsMessage } from '../../internal/views/common/SearchResultsMessage';
import { SearchSuggestion } from '../../internal/views/search-suggestion/SearchSuggestion';
export interface ISearchGnavInputField {
    config: {
        content?: IToutItem[];
        productTags?: string[];
        items: IItem[];
        toutItem?: IToutItem[];
        terms?: ITrendingTermItem[];
        favoriteProducts?: IItem[];
        productGridBreakpoints: IProductGridBreakpoints;
        hideProductPrice: boolean;
        hideProductBadge: boolean;
        showSubDisplayName?: boolean;
        hideProductRating: boolean;
        enableNotifyMe?: boolean;
        enableQuickView: boolean;
        enableMobileQuickView?: boolean;
        ctaType?: string;
        searchUrl?: string;
        minSearchLength?: number;
        searchDisplayFormat: string;
        gnavSearchTagsBlock?: boolean;
        showMobileInput?: boolean;
        showDesktopInput?: boolean;
        enableVoiceSearch?: boolean;
        phrases?: string[];
        silenceTimeout?: number;
        stopTimeout?: number;
        enableAutoReplenishment?: boolean;
        autoReplenishOptions?: number[];
        autoReplenishToggleType?: TOGGLE_TYPE;
        enableContentSearch?: boolean;
        enableContentArticle?: boolean;
        enableContentBanner?: boolean;
        enableContentTout?: boolean;
        enableContentVideo?: boolean;
        useSquareBrackets?: boolean;
        showClearButton?: boolean;
        componentElementsOrderConfig?: object;
    };
    toggleSearchOverlay?(): void;
    translations?: ITranslationsCollection;
}

interface IEnhancedSearchConfig {
    content?: IToutItem[];
    items?: IItem[];
    terms?: ITrendingTermItem[];
    hideProductPrice?: boolean;
    hideProductBadge?: boolean;
    showSubDisplayName?: boolean;
    hideProductRating?: boolean;
    enableNotifyMe?: boolean;
    enableQuickView?: boolean;
    enableMobileQuickView?: boolean;
    ctaType?: string;
    enableAutoReplenishment?: boolean;
    autoReplenishOptions?: number[];
    searchDisplayFormat?: string;
    autoReplenishToggleType?: TOGGLE_TYPE;
    componentElementsOrderConfig?: object;
}

export interface IContentSearchConfig extends Omit<IEnhancedSearchConfig, 'content' | 'items'> {
    articles?: IArticleObjItem[];
}

export interface ISimpleGridConfig extends Omit<IEnhancedSearchConfig, 'items'> {
    content?: IToutItem[];
    searchItems: IItem[];
    bestSellerItems: IItem[];
    productGridBreakpoints: IProductGridBreakpoints;
    searchUrl?: string;
}

@addContext()
@addConfig(['searchConfig'], mergeConfigs)
@configConsumer([
    'autoReplenishOptions',
    'autoReplenishToggleType',
    'ctaType',
    'enableAutoReplenishment',
    'enableContentSearch',
    'enableMobileQuickView',
    'enableNotifyMe',
    'enableQuickView',
    'enableVoiceSearch',
    'favoriteProducts',
    'gnavSearchTagsBlock',
    'hideProductBadge',
    'hideProductPrice',
    'hideProductRating',
    'items',
    'minSearchLength',
    'phrases',
    'searchButtonLabel',
    'searchClearLabel',
    'searchDisplayFormat',
    'searchPlaceholderLabel',
    'searchUrl',
    'showClearButton',
    'showDesktopInput',
    'showMobileInput',
    'showSubDisplayName',
    'silenceTimeout',
    'stopTimeout',
    'useSquareBrackets',
    'terms',
    'toutItem',
    'componentElementsOrderConfig'
])
@translate([
    'searchClearLabel',
    'searchPlaceholderLabel',
    'searchButtonLabel',
    'viewAllResultsLabel',
    'storiesTitle',
    'productsTitle'
])
@observer
export class SearchGnavInputField extends React.Component<ISearchGnavInputField> {
    private searchGnavInputFieldViewController: SearchGnavInputFieldViewController;
    public static defaultProps: ISearchGnavInputField;
    private inputRef: React.RefObject<HTMLInputElement> = React.createRef();
    private searchActiveScreenWrapper = classes('search-active-screen-wrapper');
    private searchGnavBoxClassnames = classes('search-gnav-box');
    private clearButtonClassnames = classes('search-clear-button');
    private voiceSearchWrapperClassnames = classes('voice-search-wrapper');
    private searchResultsButtonClassnames = classes('search-results-button');
    private searchresultsWrapperClassnames = classes('search-results-wrapper');

    constructor(props: ISearchGnavInputField) {
        super(props);
        this.searchGnavInputFieldViewController = diContainer.get(
            serviceNames.searchGnavInputFieldViewController
        );

        const {
            favoriteProducts,
            terms,
            toutItem,
            minSearchLength,
            enableContentSearch,
            enableContentArticle,
            enableContentBanner,
            enableContentTout,
            enableContentVideo,
            searchDisplayFormat
        } = this.props.config;

        const propsSearchConfig: IPropsSearchConfig = {
            enhancedConfig: {
                favoriteProducts,
                terms,
                toutItem
            },
            minSearchLength,
            enableContentSearch,
            searchDisplayFormat,
            contentEnabledConfig: {
                enableContentArticle,
                enableContentBanner,
                enableContentTout,
                enableContentVideo
            }
        };

        this.searchGnavInputFieldViewController.initialize(propsSearchConfig);
    }

    public componentDidUpdate() {
        const {
            setShouldFocusOverlayInput,
            data: { shouldFocusOverlayInput }
        } = this.searchGnavInputFieldViewController;

        if (shouldFocusOverlayInput) {
            this.inputRef.current && (this.inputRef.current as HTMLInputElement).focus();
            setShouldFocusOverlayInput(false);
        }
    }

    public onKeyUp = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Escape') {
            this.toggleSearchOverlay();
        }
        if (event.key === 'Enter' && this.searchGnavInputFieldViewController.data.inputValue) {
            const searchUrl = this.props.config.searchUrl;
            const searchParam = SEARCH_PARAM;
            const value = event.currentTarget.value;
            let redirect = false;
            let siteUrl = `${searchUrl}${searchParam}${value}`;

            if (!!this.searchGnavInputFieldViewController.endecaRedirectUrl) {
                siteUrl = `${this.searchGnavInputFieldViewController.endecaRedirectUrl}${searchParam}${value}${SEARCH_TYPE_PARAM}${PREDICTIVE_SEARCH_PARAM}${SEARCH_REDIRECT_PARAM}`;
                redirect = true;
            }

            this.searchGnavInputFieldViewController.onKeyUp(
                { key: event.key, value },
                siteUrl,
                redirect,
                this.props.config.enableContentSearch || false
            );
        }
    };

    public handleChange = (event: React.FormEvent<HTMLInputElement>) => {
        const value = event.currentTarget.value;
        const body = document.body;
        if ((this.props.config.gnavSearchTagsBlock && value) || value !== '') {
            body.classList.remove(`${GNAV_SEARCH_TAG_BLOCKS}`);
        } else if (this.props.config.gnavSearchTagsBlock) {
            body.classList.add(`${GNAV_SEARCH_TAG_BLOCKS}`);
        }
        this.searchGnavInputFieldViewController.handleChange({ value });
    };

    public get showSearchResults() {
        if (!!this.searchGnavInputFieldViewController.endecaRedirectUrl) {
            return false;
        }

        if (
            this.searchGnavInputFieldViewController.data.inputValue &&
            (this.props.config.items ||
                this.searchGnavInputFieldViewController.data.items.length > 0)
        ) {
            return true;
        }

        return false;
    }

    public toggleSearchOverlay = () => {
        this.searchGnavInputFieldViewController.toggleSearchOverlay();
        this.updateBodyClassList();
    };

    public closeOverlayWrapper = (e: React.SyntheticEvent<HTMLDivElement>) => {
        const target = e.target as HTMLTextAreaElement;
        if (target.className.includes(this.searchActiveScreenWrapper)) {
            this.toggleSearchOverlay();
        }
    };

    public openSearchOverlay = () => {
        this.searchGnavInputFieldViewController.openSearchOverlay();
        this.updateBodyClassList();
    };

    private updateBodyClassList() {
        const body = document.body;
        const html = document.documentElement;
        if (this.searchGnavInputFieldViewController.isSearchOverlayOpen) {
            html.classList.add('search-overlay-displayed');
            body.classList.add('search-overlay-displayed');
            if (this.props.config.gnavSearchTagsBlock) {
                body.classList.add(`${GNAV_SEARCH_TAG_BLOCKS}`);
            }
        } else {
            html.classList.remove('search-overlay-displayed');
            body.classList.remove('search-overlay-displayed');
            if (this.props.config.gnavSearchTagsBlock) {
                body.classList.remove(`${GNAV_SEARCH_TAG_BLOCKS}`);
            }
        }
    }

    @action
    public onOverlayScroll = (e: React.SyntheticEvent<HTMLDivElement>) => {
        if (e.currentTarget.scrollTop > 10) {
            this.inputRef.current && (this.inputRef.current as HTMLInputElement).blur();
        }
    };

    private renderOverlayInput = () => {
        const {
            clearInputValue,
            data: { inputValue, disableInput }
        } = this.searchGnavInputFieldViewController;

        const { enableVoiceSearch, showClearButton } = this.props.config;

        const searchInputFieldClassnames = classes('input-search-field', {
            'voice-search-enabled': enableVoiceSearch
        });

        const { searchClearLabel, searchPlaceholderLabel, searchButtonLabel } = this.props
            .translations as ITranslationsCollection;

        return (
            <>
                <InputFieldSearch
                    disabled={disableInput}
                    name="search-input"
                    type="text"
                    placeholder={searchPlaceholderLabel}
                    onChange={this.handleChange}
                    value={inputValue}
                    className={searchInputFieldClassnames}
                    onKeyUp={this.onKeyUp}
                    data-test-id="SEA-Keyword-Text"
                    aria-label={searchPlaceholderLabel}
                    title={searchButtonLabel}
                    ref={this.inputRef}
                />
                {showClearButton && inputValue && (
                    <ClearButton
                        data-test-id="SEA-Clear"
                        className={this.clearButtonClassnames}
                        onClick={clearInputValue}
                    >
                        {searchClearLabel}
                    </ClearButton>
                )}
            </>
        );
    };

    private renderTabs(
        simpleGridConfig: ISimpleGridConfig,
        contentSearchConfig: IContentSearchConfig
    ) {
        const {
            data: {
                currentTab,
                resultsLoaded,
                items,
                dynamicArticles,
                articlesRecsNum,
                productRecsNum,
                resultType,
                overlayArticlesRecordCount
            },
            setCurrentTab,
            isLoading
        } = this.searchGnavInputFieldViewController;

        const { productsTitle, storiesTitle } = this.props.translations as ITranslationsCollection;

        const { useSquareBrackets } = this.props.config;

        return items.length === 0 && dynamicArticles.length === 0 ? (
            this.renderSearchGrid(simpleGridConfig)
        ) : (
            <>
                {resultsLoaded || dynamicArticles.length ? (
                    <SearchTabs currentTabId={currentTab} onSelect={setCurrentTab}>
                        {productRecsNum && (
                            <SearchTab
                                id={TAB_TYPE.PRODUCTS}
                                title={`${productsTitle} ${formatCount(
                                    productRecsNum,
                                    useSquareBrackets
                                )}`}
                            >
                                {this.renderSearchGrid(simpleGridConfig)}
                            </SearchTab>
                        )}
                        {articlesRecsNum && (
                            <SearchTab
                                id={TAB_TYPE.STORIES}
                                title={`${storiesTitle} ${formatCount(
                                    articlesRecsNum,
                                    useSquareBrackets
                                )}`}
                            >
                                <SearchResultsMessage
                                    recsCount={overlayArticlesRecordCount}
                                    totalNumRecs={articlesRecsNum}
                                    resultType={resultType}
                                />
                                <SearchContentGrid
                                    config={contentSearchConfig}
                                    view={SEARCH_VIEW.OVERLAY_ARTICLES}
                                />
                            </SearchTab>
                        )}
                    </SearchTabs>
                ) : (
                    <Spinner data-test-id="SEA-SpinnerIcon" isLoading={isLoading} />
                )}
            </>
        );
    }

    private renderSearchGrid(simpleGridConfig: ISimpleGridConfig) {
        const {
            data,
            searchTermSelected,
            resultType,
            isLoading
        } = this.searchGnavInputFieldViewController;

        const {
            inputValue,
            limitedTypeaheadResults,
            numRecs,
            adjustedTerm,
            overlayProductsRecordCount,
            showSearchGrid
        } = data;

        return (
            showSearchGrid && (
                <SearchResultsWrapper
                    suggestionsCount={limitedTypeaheadResults.length}
                    isLoading={isLoading}
                    className={this.searchresultsWrapperClassnames}
                >
                    <SearchSuggestion
                        searchUrl={this.props.config.searchUrl}
                        limitedTypeaheadResults={limitedTypeaheadResults}
                        searchTermSelected={searchTermSelected}
                        isLoading={isLoading}
                    />
                    <SearchResultsMessage
                        inputValue={inputValue}
                        recsCount={overlayProductsRecordCount}
                        totalNumRecs={numRecs.totalNumRecs}
                        resultType={resultType}
                        adjustedTerm={adjustedTerm}
                        viewType={VIEW_TYPE.GNAV}
                        isLoading={isLoading}
                    />
                    <SearchGrid
                        config={simpleGridConfig}
                        isLoading={isLoading}
                        resultsWithSuggestions={!!limitedTypeaheadResults.length}
                    />
                </SearchResultsWrapper>
            )
        );
    }

    private renderSearchBlock() {
        const { viewAllResultsLabel } = this.props.translations as ITranslationsCollection;

        const {
            data: { inputValue, isSearchButtonVisible, searchType },
            onSearchButtonClick
        } = this.searchGnavInputFieldViewController;

        const { searchUrl, showDesktopInput } = this.props.config;

        const resultsUrl = [
            searchUrl,
            SEARCH_PARAM,
            inputValue,
            SEARCH_TYPE_PARAM,
            searchType
        ].join('');

        return (
            <SearchBoxWrapper className={this.searchGnavBoxClassnames}>
                <SearchCloseActiveScreen toggleSearchOverlay={this.toggleSearchOverlay} />
                <Mobile>{this.renderOverlayInput()}</Mobile>
                {!showDesktopInput && <Desktop>{this.renderOverlayInput()}</Desktop>}
                {isSearchButtonVisible && (
                    <GnavSearchButton
                        aria-label={viewAllResultsLabel}
                        title={viewAllResultsLabel}
                        data-test-id="SEA-Search-Button-Overlay"
                        className={this.searchResultsButtonClassnames}
                        onClick={() => onSearchButtonClick(resultsUrl)}
                    >
                        {viewAllResultsLabel}
                    </GnavSearchButton>
                )}
            </SearchBoxWrapper>
        );
    }

    public render() {
        const { data } = this.searchGnavInputFieldViewController;

        const {
            limitItems,
            inputValue,
            isSearchOverlayOpen,
            configSearch,
            dynamicArticles,
            disableInput,
            onlyFavoritesEnabled,
            onlyTrendingTermsEnabled,
            showEnhancedSearch
        } = data;

        const {
            searchUrl,
            enableVoiceSearch,
            phrases,
            silenceTimeout,
            stopTimeout,
            showMobileInput,
            showDesktopInput,
            items: bestSellerItems,
            enableContentSearch,
            toutItem,
            terms,
            favoriteProducts
        } = this.props.config;

        const searchConfig = {
            ...this.props.config,
            searchItems: limitItems,
            bestSellerItems,
            searchUrl
        };

        const enhancedSearchConfig = {
            ...this.props.config,
            content: toutItem,
            items: favoriteProducts,
            trendingTerms: terms,
            onlyTrendingTermsEnabled,
            onlyFavoritesEnabled
        };

        const contentSearchConfig = {
            ...this.props.config,
            articles: dynamicArticles.slice(0, CONTENT_SEARCH_ITEMS_LIMIT),
            searchTerm: inputValue
        };

        let voiceSearchConfig;
        if (searchUrl && phrases && silenceTimeout && stopTimeout) {
            voiceSearchConfig = {
                searchUrl,
                phrases,
                silenceTimeout,
                stopTimeout
            };
        }

        const inputConfig = {
            disabled: disableInput,
            onKeyUp: this.onKeyUp,
            onChange: this.handleChange,
            inputValue
        };

        return (
            <>
                <SearchOverlayActiveScreen
                    openSearchOverlay={this.openSearchOverlay}
                    isSearchOverlayOpen={isSearchOverlayOpen}
                    showMobileInput={showMobileInput}
                    showDesktopInput={showDesktopInput}
                    inputConfig={inputConfig}
                />
                {isSearchOverlayOpen && (
                    <SearchActiveScreenWrapper
                        className={this.searchActiveScreenWrapper}
                        onScroll={this.onOverlayScroll}
                        onClick={this.closeOverlayWrapper}
                    >
                        {this.renderSearchBlock()}
                        {enableContentSearch &&
                            this.showSearchResults &&
                            dynamicArticles.length > 0 &&
                            this.renderTabs(searchConfig, contentSearchConfig)}
                        {enableContentSearch &&
                            this.showSearchResults &&
                            dynamicArticles.length === 0 &&
                            this.renderSearchGrid(searchConfig)}
                        {!enableContentSearch &&
                            this.showSearchResults &&
                            this.renderSearchGrid(searchConfig)}
                        {showEnhancedSearch && !this.showSearchResults && (
                            <SearchContentGrid
                                config={enhancedSearchConfig}
                                view={SEARCH_VIEW.ENHANCED_SEARCH_OVERLAY}
                            />
                        )}
                        {!!(enableVoiceSearch && configSearch.voiceSearch.enabled) &&
                            voiceSearchConfig && (
                                <div className={this.voiceSearchWrapperClassnames}>
                                    <VoiceSearch config={voiceSearchConfig} />
                                </div>
                            )}
                    </SearchActiveScreenWrapper>
                )}
            </>
        );
    }
}

export default SearchGnavInputField;
